import React, { useMemo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import useSWR from 'swr';
import { trackClickedSubscribeCTA, getUserFavorites } from '@surfline/web-common';

import { doFavoriteSpot, doUnfavoriteSpot } from 'actions/user';
import { fetchReport } from 'common/api/spot';
import FavoritesIndicator from 'components/Icons/FavoritesIndicator';
import PremiumCamCTA from 'components/PremiumCamCTA';
import config from 'config';
import type { Camera } from 'types/camera';
import type { FavoriteItem } from 'types/favorites';
import type { SpotReportData } from 'types/spot';
import createAccessibleOnClick, { BTN } from 'utils/createAccessibleOnClick';
import { getCamTitle } from 'utils/getCamTitle';
import conditionClassModifier from 'utils/conditionClassModifier';
import HoverMenu from '../../HoverMenu';
import { useTimeout } from '../hooks/useTimeout';

import CamFiller from './CamFiller';
import MultiCamIcon from '../icons/MultiCam';
import FullscreenIcon from '../icons/FullscreenIcon';
import ConditionsOverlay from '../../ConditionsOverlay';

const overlayClass = (condition: string, visible: boolean) => {
  const conditionClass = condition ? conditionClassModifier(condition) : 'none';
  return classNames({
    'sl-cam-overlay': true,
    'sl-cam-overlay--visible': visible,
    [`sl-cam-overlay--${conditionClass}`]: conditionClass,
  });
};

const topOverlayClass = (visible: boolean) =>
  classNames({
    'sl-cam-overlay__top': true,
    'sl-cam-overlay__top--visible': visible,
  });

const onClickedSubscribeCTA = () =>
  trackClickedSubscribeCTA({
    location: 'premium only cam - multi',
    category: 'cams & reports',
    pageName: 'multi-cam',
  });

interface Spot extends SpotReportData {
  isFiller: boolean;
}

interface Props {
  spot: Spot;
  camIndex: number;
  changeCamAngle: (cam: Camera) => void;
  isFullscreen: boolean;
  toggleFullscreen: () => void;
  camAngle?: Camera;
  showingPremiumCamCTA: boolean;
}

const CamOverlay = ({
  spot,
  camIndex,
  isFullscreen = false,
  toggleFullscreen = () => {},
  changeCamAngle,
  camAngle,
  showingPremiumCamCTA = false,
}: Props) => {
  const dispatch = useDispatch();
  const favorites: FavoriteItem[] = useSelector(getUserFavorites);
  const [visible, setVisible] = useState(true);

  const { data: spotData } = useSWR(spot?._id ? { spotId: spot._id } : undefined, fetchReport, {
    refreshInterval: config.multiCamConditionsPollingInterval,
    dedupingInterval: config.multiCamConditionsPollingInterval - 1,
    revalidateOnFocus: true,
    focusThrottleInterval: config.multiCamConditionsPollingInterval,
  });

  const isFavorite = useMemo(
    () => !!favorites.find((favorite) => favorite._id === spot._id),
    [favorites, spot._id],
  );
  const doToggleFavorite = useCallback(() => {
    const spotId = spot._id;
    return dispatch(
      isFavorite ? (doUnfavoriteSpot(spotId) as any) : (doFavoriteSpot(spotId) as any),
    );
  }, [dispatch, isFavorite, spot]);

  const { isFiller, cameras, conditions } = spot;
  const hasCam = cameras?.length > 0;
  const hasMultipleCams = cameras?.length > 1;
  const menuOptions = hasMultipleCams
    ? cameras.map((cam) => ({
        selected: getCamTitle(camAngle?.title) === getCamTitle(cam.title),
        value: cam,
        name: getCamTitle(cam.title),
      }))
    : [];

  // This hook fades the cam controls after 8 seconds on mount
  useTimeout({
    timeoutLength: 8000,
    initialState: true,
    cb: () => setVisible(false),
  });

  return (
    <div className={overlayClass(conditions?.value ?? '', visible)}>
      {showingPremiumCamCTA && (
        <PremiumCamCTA
          href="/upgrade"
          camStillUrl={camAngle?.stillUrl}
          onClickPaywall={onClickedSubscribeCTA}
        />
      )}
      {isFiller && <CamFiller camIndex={camIndex} />}
      <div className="sl-cam-overlay__bottom">
        {!isFiller && !showingPremiumCamCTA && (spotData as React.ReactNode) && (
          <ConditionsOverlay
            visible={visible}
            spot={spot}
            forecast={(spotData as any).forecast}
            camAngle={camAngle}
          />
        )}
      </div>
      <div className={topOverlayClass(visible)}>
        {hasMultipleCams && (
          <HoverMenu
            Icon={MultiCamIcon as any}
            menuTitle="Cam Angle"
            menuOptions={menuOptions}
            onClickMenuItem={changeCamAngle}
            defaultValue={getCamTitle(camAngle?.title)}
            useSelectOnMobile
            selectDescription="- SELECT A CAM ANGLE -"
          />
        )}
        {!hasCam && !isFiller && <div className="sl-cam-overlay__no-cam">no cam</div>}
        <div
          className="sl-cam-overlay__favorite-toggle"
          title={isFavorite ? 'Remove Favorite' : 'Add Favorite'}
          {...createAccessibleOnClick(doToggleFavorite, BTN)}
        >
          {!isFiller && <FavoritesIndicator favorited={isFavorite} />}
        </div>
        <div
          className="sl-cam-overlay__fullscreen-icon"
          title="Toggle Fullscreen"
          {...createAccessibleOnClick(toggleFullscreen, BTN)}
        >
          {!isFiller && <FullscreenIcon isFullscreen={isFullscreen} />}
        </div>
      </div>
    </div>
  );
};

export default CamOverlay;
